import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  HireEmployeeApprovalUser,
  HireEmployeeRequestCreatePayload,
  HireEmployeeRequestByIdResponse,
  HireEmployeeRequestListResponse,
} from '@/modules/hire-employee/types'

function mapApprovalUserRow(raw: Record<string, unknown>): HireEmployeeApprovalUser {
  const actionDateRaw = raw.actionDate ?? raw.ActionDate
  const noteRaw = raw.approvalNote ?? raw.ApprovalNote
  return {
    id: Number(raw.id ?? raw.Id ?? 0),
    userName: String(raw.userName ?? raw.UserName ?? ''),
    empId: Number(raw.empId ?? raw.EmpId ?? 0),
    branchId: Number(raw.branchId ?? raw.BranchId ?? 0),
    actionDate: actionDateRaw != null && String(actionDateRaw).trim() !== '' ? String(actionDateRaw) : null,
    action: String(raw.action ?? raw.Action ?? ''),
    approvalNote: noteRaw != null && String(noteRaw).trim() !== '' ? String(noteRaw) : null,
    departmentName: String(raw.departmentName ?? raw.DepartmentName ?? ''),
  }
}

function extractApprovalUsersArray(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload
  if (payload && typeof payload === 'object') {
    const o = payload as Record<string, unknown>
    if (Array.isArray(o.data)) return o.data
    if (Array.isArray(o.Data)) return o.Data
  }
  return []
}

function appendField(fd: FormData, key: string, value: unknown) {
  if (value === undefined || value === null) {
    fd.append(key, '')
    return
  }
  if (value instanceof File) {
    fd.append(key, value)
    return
  }
  fd.append(key, String(value))
}

function snapshotFormData(fd: FormData): Record<string, string | Record<string, unknown>> {
  const out: Record<string, string | Record<string, unknown>> = {}
  fd.forEach((value, key) => {
    if (value instanceof File) {
      out[key] = { fileName: value.name, size: value.size, type: value.type }
    } else {
      out[key] = String(value)
    }
  })
  return out
}

export const hireEmployeeRequestService = {
  async createRequest(payload: HireEmployeeRequestCreatePayload): Promise<unknown> {
    const fd = new FormData()

    // IMPORTANT: keys must match Swagger exactly
    appendField(fd, 'Id', payload.id)
    appendField(fd, 'UserId', payload.userId)
    appendField(fd, 'JobId', payload.jobId)
    appendField(fd, 'EmpFirstNameEn', payload.empFirstNameEn)
    appendField(fd, 'EmpFatherNameEn', payload.empFatherNameEn)
    appendField(fd, 'EmpGrandNameEn', payload.empGrandNameEn)
    appendField(fd, 'EmpFamilyNameEn', payload.empFamilyNameEn)
    appendField(fd, 'EmpFirstNameAr', payload.empFirstNameAr)
    appendField(fd, 'EmpFatherNameAr', payload.empFatherNameAr)
    appendField(fd, 'EmpGrandNameAr', payload.empGrandNameAr)
    appendField(fd, 'EmpFamilyNameAr', payload.empFamilyNameAr)
    appendField(fd, 'Position', payload.position)
    appendField(fd, 'SalaryRangeFrom', payload.salaryRangeFrom)
    appendField(fd, 'SalaryRangeTo', payload.salaryRangeTo)
    appendField(fd, 'TrainingCourses', payload.trainingCourses)
    appendField(fd, 'YearsOfExperience', payload.yearsOfExperience)
    appendField(fd, 'Details', payload.details)
    appendField(fd, 'OtherRequiredQualification', payload.otherRequiredQualification)
    appendField(fd, 'EducationalQualification', payload.educationalQualification)
    if (payload.hireEmpImg) {
      fd.append('HireEmpImg', payload.hireEmpImg)
    } else {
      appendField(fd, 'HireEmpImg', '')
    }
    appendField(fd, 'BranchId', payload.branchId)

    // eslint-disable-next-line no-console
    console.groupCollapsed(
      '%c[HireEmployeeRequest/CreateRequest] Outgoing payload',
      'background:#1d4ed8;color:#fff;font-weight:bold;padding:2px 8px;border-radius:4px',
    )
    // eslint-disable-next-line no-console
    console.log('URL:', endpoints.hireEmployeeRequest.create)
    // eslint-disable-next-line no-console
    console.log('Structure (logical fields):', {
      Id: payload.id,
      UserId: payload.userId,
      JobId: payload.jobId,
      EmpFirstNameEn: payload.empFirstNameEn,
      EmpFatherNameEn: payload.empFatherNameEn,
      EmpGrandNameEn: payload.empGrandNameEn,
      EmpFamilyNameEn: payload.empFamilyNameEn,
      EmpFirstNameAr: payload.empFirstNameAr,
      EmpFatherNameAr: payload.empFatherNameAr,
      EmpGrandNameAr: payload.empGrandNameAr,
      EmpFamilyNameAr: payload.empFamilyNameAr,
      Position: payload.position,
      SalaryRangeFrom: payload.salaryRangeFrom,
      SalaryRangeTo: payload.salaryRangeTo,
      TrainingCourses: payload.trainingCourses,
      YearsOfExperience: payload.yearsOfExperience,
      Details: payload.details,
      OtherRequiredQualification: payload.otherRequiredQualification,
      EducationalQualification: payload.educationalQualification,
      HireEmpImg: payload.hireEmpImg
        ? { name: payload.hireEmpImg.name, size: payload.hireEmpImg.size, type: payload.hireEmpImg.type }
        : '(empty — field sent as "")',
      BranchId: payload.branchId,
    })
    // eslint-disable-next-line no-console
    console.log('FormData snapshot (key → value as sent):', snapshotFormData(fd))
    // eslint-disable-next-line no-console
    console.groupEnd()

    const res = await apiClient.post(endpoints.hireEmployeeRequest.create, fd)

    // eslint-disable-next-line no-console
    console.groupCollapsed(
      '%c[HireEmployeeRequest/CreateRequest] API response',
      'background:#15803d;color:#fff;font-weight:bold;padding:2px 8px;border-radius:4px',
    )
    // eslint-disable-next-line no-console
    console.log('HTTP status:', res.status)
    // eslint-disable-next-line no-console
    console.log('response.data (exact):', res.data)
    try {
      // eslint-disable-next-line no-console
      console.log('response.data (JSON):', JSON.stringify(res.data, null, 2))
    } catch {
      // eslint-disable-next-line no-console
      console.log('response.data (JSON): <could not stringify>')
    }
    // eslint-disable-next-line no-console
    console.groupEnd()

    return res.data
  },

  async getStatusLookups(): Promise<Array<{ id?: number; lookupCodeId?: number; lookupNameEn?: string; lookupNameAr?: string }>> {
    const res = await apiClient.get(endpoints.hireEmployeeRequest.getStatusLookups)
    return (res.data || []) as Array<{ id?: number; lookupCodeId?: number; lookupNameEn?: string; lookupNameAr?: string }>
  },

  async createEmployeeFromRequest(id: number): Promise<unknown> {
    const res = await apiClient.put(endpoints.hireEmployeeRequest.createEmployeeFromRequest(id))
    return res.data
  },

  async delete(id: number): Promise<unknown> {
    const res = await apiClient.delete(endpoints.hireEmployeeRequest.delete(id))
    return res.data
  },

  async getAll(
    branchId: number,
    pageNumber: number,
    pageSize: number,
    status?: number | string
  ): Promise<HireEmployeeRequestListResponse> {
    const res = await apiClient.get(endpoints.hireEmployeeRequest.getAll(branchId, pageNumber, pageSize, status))
    return res.data as HireEmployeeRequestListResponse
  },

  async getById(id: number, branchId: number): Promise<HireEmployeeRequestByIdResponse> {
    const res = await apiClient.get(endpoints.hireEmployeeRequest.getById(id, branchId))
    return res.data as HireEmployeeRequestByIdResponse
  },

  async getApprovalUsers(reqId: number): Promise<HireEmployeeApprovalUser[]> {
    const res = await apiClient.get(endpoints.hireEmployeeRequest.getApprovalUsers(reqId))
    const list = extractApprovalUsersArray(res.data)
    return list
      .filter((x): x is Record<string, unknown> => x != null && typeof x === 'object' && !Array.isArray(x))
      .map((row) => mapApprovalUserRow(row as Record<string, unknown>))
  },
}

